import { useCallback, useEffect, useRef, useState } from 'react';

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

/**
 * The one global binding: Cmd/Ctrl+K opens the command palette, everywhere, including from inside a
 * text field — which is the point of choosing a modified key for it.
 *
 * It used to carry `c`, `/` and `1`/`2`/`3` as well. Those bound the legacy feedback workspace's
 * layouts, which no route reaches any more, and the pages that do have their own list shortcuts
 * (`j`/`k`/`enter`/`o`/`c`) own them locally where they can say so on screen. A global handler that
 * silently competed with those is worse than no handler: the palette is where a binding is taught,
 * so a binding the palette cannot name should not exist.
 *
 * `openBar` answers ⌘K first where the app's one bar is at the top (F-0743 §4, VSB-34): it returns
 * whether it took the caret, and only where it did not does the palette open.
 */
export function useKeyboardShortcuts({ openBar }: { openBar?: () => boolean } = {}) {
  const [isCommandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const openBarRef = useRef(openBar);
  openBarRef.current = openBar;

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      if (openBarRef.current?.()) return;
      setCommandPaletteOpen((prev) => !prev);
      return;
    }
    if (e.key === 'Escape') {
      setCommandPaletteOpen(false);
      return;
    }
    if (isInputFocused()) return;
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return { isCommandPaletteOpen, setCommandPaletteOpen };
}
